import { CheckCheck, ClipboardCheck, Info, TriangleAlert } from 'lucide-react'
import { useMemo, useState } from 'react'

import type {
  AttendanceStatus,
  GroupDetail,
  GroupMeeting,
  GroupMember,
  RecordAttendanceRequest,
} from '@/api/types'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { ProblemAlert } from '@/components/error-surface'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formatShortDate } from '@/lib/datetime'
import { cn } from '@/lib/utils'

import {
  ATTENDANCE_STATUSES,
  ATTENDANCE_STATUS_EFFECT,
  ATTENDANCE_STATUS_LABELS,
  formatTimeOfDay,
} from './constants'
import { useRecordMeetingAttendance } from './mutations'

/**
 * The attendance register.
 *
 * This screen works at every width, which is a decision rather than a default — see
 * docs/mobile-policy.md. The short of it: every input is a choice among four rather than a keyed
 * amount, nothing here is money, and the one honest weakness of a narrow screen — that eighteen
 * rows cannot be checked at a glance before they commit — is answered by the confirmation, which
 * states the totals and says the register cannot be recorded twice.
 *
 * The other thing this screen must not do is invent evidence. The field application records a GPS
 * fix inside the geofence and the server re-validates it; a browser has no fix, sends none, and is
 * not refused for it. A register taken from four metres away and one taken from a desk in town
 * must not look the same afterwards, so this one is honest about being the second kind.
 */

type Marks = Record<string, { status: AttendanceStatus; note: string }>

/**
 * Who could have been at this meeting.
 *
 * Membership is dated, and a register is a record of a particular day: somebody who joined last
 * week was not at last month's meeting, and somebody who left in March was at February's. The API
 * refuses an entry naming a non-member on that date as `groups.attendance_member_not_in_group`, so
 * the sheet is shaped to the meeting's own date rather than to today's membership.
 */
function membersOnDate(members: GroupMember[], date: string): GroupMember[] {
  return members.filter(
    (member) => member.joinedDate <= date && (member.leftDate === null || member.leftDate >= date),
  )
}

export function AttendanceSheet({
  group,
  meeting,
  onRecorded,
}: {
  group: GroupDetail
  meeting: GroupMeeting
  onRecorded: () => void
}) {
  const eligible = useMemo(
    () => membersOnDate(group.members, meeting.scheduledDate),
    [group.members, meeting.scheduledDate],
  )

  const [met, setMet] = useState(true)
  const [notes, setNotes] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [marks, setMarks] = useState<Marks>(() =>
    Object.fromEntries(
      eligible.map((member) => [member.customerId, { status: 'Present', note: '' }]),
    ),
  )

  const record = useRecordMeetingAttendance(group.id, meeting.id)

  const tally = useMemo(() => {
    const counts: Record<AttendanceStatus, number> = { Present: 0, Late: 0, Absent: 0, Excused: 0 }
    for (const member of eligible) {
      const status = marks[member.customerId]?.status ?? 'Present'
      counts[status] += 1
    }
    return counts
  }, [eligible, marks])

  // The ratio the health score is computed from: excused absences are excluded from *both* sides
  // rather than counted against the group. Showing it here is the point — an officer who believes
  // an honest "excused" will be held against the group stops recording them.
  const denominator = eligible.length - tally.Excused
  const numerator = tally.Present + tally.Late

  const setStatus = (customerId: string, status: AttendanceStatus) => {
    setMarks((current) => ({
      ...current,
      [customerId]: { status, note: current[customerId]?.note ?? '' },
    }))
  }

  const setNote = (customerId: string, note: string) => {
    setMarks((current) => ({
      ...current,
      [customerId]: { status: current[customerId]?.status ?? 'Present', note },
    }))
  }

  const markAll = (status: AttendanceStatus) => {
    setMarks(
      Object.fromEntries(
        eligible.map((member) => [
          member.customerId,
          { status, note: marks[member.customerId]?.note ?? '' },
        ]),
      ),
    )
  }

  const request: RecordAttendanceRequest = met
    ? {
        status: 'Completed',
        notes: notes.trim() === '' ? null : notes.trim(),
        entries: eligible.map((member) => ({
          customerId: member.customerId,
          status: marks[member.customerId]?.status ?? 'Present',
          note:
            marks[member.customerId]?.note.trim() === '' || marks[member.customerId] === undefined
              ? null
              : marks[member.customerId].note.trim(),
        })),
      }
    : // A missed meeting takes no entries: the group did not meet, so there is nobody to mark.
      { status: 'Missed', notes: notes.trim() === '' ? null : notes.trim(), entries: [] }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="min-w-0">
            <CardTitle>Did the group meet?</CardTitle>
            <p className="mt-0.5 text-xs text-muted">
              Recording that a group did not meet is a real answer, not a blank register. It counts
              in the attendance denominator, which is how a run of missed meetings shows up in the
              health score.
            </p>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button variant={met ? 'primary' : 'secondary'} onClick={() => setMet(true)}>
              The group met
            </Button>
            <Button variant={met ? 'secondary' : 'danger'} onClick={() => setMet(false)}>
              The group did not meet
            </Button>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="attendance-notes">Notes</Label>
            <Input
              id="attendance-notes"
              value={notes}
              autoComplete="off"
              placeholder={met ? 'Two members travelling' : 'Market day moved; nobody came'}
              onChange={(event) => setNotes(event.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {met ? (
        <>
          <Card>
            <CardHeader className="flex-wrap gap-3">
              <div className="min-w-0">
                <CardTitle>Register</CardTitle>
                <p className="mt-0.5 text-xs text-muted">
                  {eligible.length} member{eligible.length === 1 ? '' : 's'} were in this group on{' '}
                  {formatShortDate(meeting.scheduledDate)}. Somebody who joined afterwards is not on
                  the sheet.
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" size="sm" onClick={() => markAll('Present')}>
                  <CheckCheck />
                  Mark all present
                </Button>
                <Button variant="secondary" size="sm" onClick={() => markAll('Absent')}>
                  Mark all absent
                </Button>
              </div>
            </CardHeader>

            <CardContent className="space-y-3">
              {eligible.length === 0 ? (
                <p className="text-sm text-body">
                  Nobody was a member of this group on {formatShortDate(meeting.scheduledDate)}, so
                  there is no register to take. Record the meeting as missed, or admit members first.
                </p>
              ) : (
                <ul className="space-y-2">
                  {eligible.map((member) => (
                    <li key={member.customerId}>
                      <MemberMark
                        member={member}
                        status={marks[member.customerId]?.status ?? 'Present'}
                        note={marks[member.customerId]?.note ?? ''}
                        onStatusChange={(status) => setStatus(member.customerId, status)}
                        onNoteChange={(note) => setNote(member.customerId, note)}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Alert tone="info">
            <Info aria-hidden />
            <div className="space-y-1">
              <AlertTitle>What each mark does to the score</AlertTitle>
              <AlertDescription>
                <ul className="space-y-0.5">
                  {ATTENDANCE_STATUSES.map((status) => (
                    <li key={status}>
                      <span className="font-medium text-heading">
                        {ATTENDANCE_STATUS_LABELS[status]}
                      </span>{' '}
                      — {ATTENDANCE_STATUS_EFFECT[status]}
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </div>
          </Alert>
        </>
      ) : null}

      {record.isError ? <ProblemAlert error={record.error} /> : null}

      {/*
        The tally and the commit are one block, and it sticks to the bottom of the viewport at
        every width. This is what replaces "seeing the whole register at once" on a narrow screen:
        the operator reads five numbers that describe eighteen rows without scrolling back, and
        those are the numbers the confirmation repeats. It is kept deliberately short — a summary
        that eats a quarter of a phone screen is one that gets scrolled past.
      */}
      <Card className="sticky bottom-0">
        <CardContent className="flex flex-wrap items-center gap-3 p-3">
          {met ? (
            <dl className="flex min-w-0 flex-1 flex-wrap gap-x-4 gap-y-1">
              {ATTENDANCE_STATUSES.map((status) => (
                <div key={status}>
                  <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">
                    {ATTENDANCE_STATUS_LABELS[status]}
                  </dt>
                  <dd className="numeric text-base font-semibold text-heading tabular-nums lg:text-lg">
                    {tally[status]}
                  </dd>
                </div>
              ))}
              <div>
                <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">
                  Attendance
                </dt>
                <dd className="numeric text-base font-semibold text-heading tabular-nums lg:text-lg">
                  {denominator === 0 ? '—' : `${((numerator / denominator) * 100).toFixed(0)}%`}
                </dd>
              </div>
            </dl>
          ) : (
            <p className="min-w-0 flex-1 text-sm text-body">
              This meeting will be recorded as missed. No register is taken.
            </p>
          )}

          <Button
            variant="primary"
            onClick={() => setConfirming(true)}
            disabled={record.isPending || (met && eligible.length === 0)}
          >
            <ClipboardCheck />
            Review and record
          </Button>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={met ? 'Record this register' : 'Record this meeting as missed'}
        description={`${group.name} — ${formatShortDate(meeting.scheduledDate)} at ${formatTimeOfDay(meeting.scheduledTime)}.`}
        confirmLabel={met ? 'Record register' : 'Record as missed'}
        tone={met ? 'default' : 'danger'}
        isPending={record.isPending}
        error={record.isError ? record.error : null}
        onConfirm={() => {
          record.mutate(request, {
            onSuccess: () => {
              setConfirming(false)
              onRecorded()
            },
          })
        }}
      >
        <div className="space-y-3">
          {met ? (
            <dl className="grid grid-cols-2 gap-3 rounded-card border border-border-subtle bg-surface-muted p-3">
              {ATTENDANCE_STATUSES.map((status) => (
                <div key={status}>
                  <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">
                    {ATTENDANCE_STATUS_LABELS[status]}
                  </dt>
                  <dd className="numeric text-base font-semibold text-heading tabular-nums">
                    {tally[status]}
                  </dd>
                </div>
              ))}
              <div className="col-span-2">
                <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">
                  Of {eligible.length} member{eligible.length === 1 ? '' : 's'} on the sheet
                </dt>
                <dd className="text-sm text-body">
                  {numerator} attended, {tally.Absent} did not, {tally.Excused} excused and excluded
                  from the ratio.
                </dd>
              </div>
            </dl>
          ) : null}

          <Alert tone="warning">
            <TriangleAlert aria-hidden />
            <div className="space-y-1">
              <AlertTitle>A register is recorded once</AlertTitle>
              <AlertDescription>
                Recording this closes the meeting. It cannot be recorded again — a second attempt is
                refused as <span className="font-mono text-xs">groups.meeting_not_open_for_attendance</span>.
                Check the numbers above against the sheet in front of you.
              </AlertDescription>
            </div>
          </Alert>

          <p className="text-xs text-muted">
            No GPS fix is sent. This browser has none, and one typed at a desk would be evidence of
            nothing — the field application records a real fix and the server re-validates it.
          </p>
        </div>
      </ConfirmDialog>
    </div>
  )
}

/**
 * One member's mark.
 *
 * Native radios under the labels rather than buttons with `role="radio"`: arrow-key movement
 * within the group, one tab stop for the whole set, and the correct announcement all come free,
 * and this control is repeated eighteen times down a page where getting any of that subtly wrong
 * would be repeated eighteen times too.
 */
function MemberMark({
  member,
  status,
  note,
  onStatusChange,
  onNoteChange,
}: {
  member: GroupMember
  status: AttendanceStatus
  note: string
  onStatusChange: (status: AttendanceStatus) => void
  onNoteChange: (note: string) => void
}) {
  const groupName = `attendance-${member.customerId}`

  return (
    <div className="space-y-2 rounded-card border border-border-subtle bg-surface p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium break-words text-heading">{member.fullName}</p>
          <p className="font-mono text-2xs text-muted">{member.customerNumber}</p>
        </div>

        <fieldset className="flex flex-wrap gap-1">
          <legend className="sr-only">Attendance for {member.fullName}</legend>
          {ATTENDANCE_STATUSES.map((candidate) => {
            const selected = candidate === status
            return (
              <label
                key={candidate}
                className={cn(
                  'flex min-h-11 cursor-pointer items-center rounded-control border px-3 text-sm font-medium transition-colors',
                  'focus-within:ring-2 focus-within:ring-brand-fill focus-within:ring-offset-1',
                  selected
                    ? candidate === 'Absent'
                      ? 'border-danger-border bg-danger-subtle text-danger-text'
                      : candidate === 'Excused'
                        ? 'border-neutral-border bg-neutral-subtle text-body'
                        : 'border-success-border bg-success-subtle text-success-text'
                    : 'border-border-subtle bg-surface text-muted hover:bg-surface-hover hover:text-body',
                )}
              >
                <input
                  type="radio"
                  name={groupName}
                  value={candidate}
                  checked={selected}
                  onChange={() => onStatusChange(candidate)}
                  className="sr-only"
                />
                {ATTENDANCE_STATUS_LABELS[candidate]}
              </label>
            )
          })}
        </fieldset>
      </div>

      {status === 'Excused' || status === 'Absent' ? (
        <Input
          value={note}
          autoComplete="off"
          placeholder={status === 'Excused' ? 'Why it was excused — funeral, illness' : 'Note, optional'}
          aria-label={`Note for ${member.fullName}`}
          onChange={(event) => onNoteChange(event.target.value)}
        />
      ) : null}
    </div>
  )
}
